/**
 * LRU Cache for Search Results
 * Provides 10-100x speedup for repeated queries (e.g., autocomplete)
 */

import type { SuggestionResult } from "./types.js";

/**
 * LRU (Least Recently Used) Cache
 * Automatically evicts oldest entries when capacity is reached
 */
export class LRUCache<K, V> {
  private cache: Map<K, V>;
  private capacity: number;

  constructor(capacity: number = 100) {
    this.cache = new Map();
    this.capacity = capacity;
  }

  /**
   * Get value from cache
   * Moves item to end (most recently used)
   */
  get(key: K): V | undefined {
    if (!this.cache.has(key)) {
      return undefined;
    }

    // Move to end (most recently used)
    const value = this.cache.get(key)!;
    this.cache.delete(key);
    this.cache.set(key, value);

    return value;
  }

  /**
   * Set value in cache
   * Evicts oldest entry if capacity exceeded
   */
  set(
    //
    key: K,
    value: V
  ): void {
    // Remove if exists (to update position)
    if (this.cache.has(key)) {
      this.cache.delete(key);
    }

    // Add to end (most recently used)
    this.cache.set(key, value);

    // Evict oldest if over capacity
    if (this.cache.size > this.capacity) {
      const firstKey = this.cache.keys().next().value as K;
      if (firstKey !== undefined) {
        this.cache.delete(firstKey);
      }
    }
  }

  /**
   * Check if key exists in cache
   */
  has(key: K): boolean {
    return this.cache.has(key);
  }

  /**
   * Clear all cached entries
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Get current cache size
   */
  get size(): number {
    return this.cache.size;
  }

  /**
   * Get cache statistics
   */
  getStats(): {
    //
    size: number;
    capacity: number;
    utilization: number;
  } {
    return {
      size: this.cache.size,
      capacity: this.capacity,
      utilization: this.cache.size / this.capacity,
    };
  }
}

/**
 * Search Result Cache
 * Caches search results with automatic invalidation
 */
export class SearchCache {
  private cache: LRUCache<string, SuggestionResult[]>;
  private hits: number = 0;
  private misses: number = 0;

  constructor(capacity: number = 100) {
    this.cache = new LRUCache(capacity);
  }

  /**
   * Generate cache key from query and options
   */
  private getCacheKey(query: string, maxResults?: number, options?: any): string {
    const optionsKey = options ? JSON.stringify(options) : "";
    return `${query}|${maxResults || "default"}|${optionsKey}`;
  }

  /**
   * Get cached results
   */
  get(query: string, maxResults?: number, options?: any): SuggestionResult[] | undefined {
    const key = this.getCacheKey(query, maxResults, options);
    const result = this.cache.get(key);

    if (result) {
      this.hits++;
    } else {
      this.misses++;
    }

    return result;
  }

  /**
   * Set cached results
   */
  set(query: string, results: SuggestionResult[], maxResults?: number, options?: any): void {
    const key = this.getCacheKey(query, maxResults, options);
    this.cache.set(key, results);
  }

  /**
   * Clear cache
   */
  clear(): void {
    this.cache.clear();
    this.hits = 0;
    this.misses = 0;
  }

  /**
   * Get cache statistics
   */
  getStats(): {
    //
    size: number;
    capacity: number;
    hits: number;
    misses: number;
    hitRate: number;
  } {
    const cacheStats = this.cache.getStats();
    const total = this.hits + this.misses;
    const hitRate = total > 0 ? this.hits / total : 0;

    return {
      ...cacheStats,
      hits: this.hits,
      misses: this.misses,
      hitRate,
    };
  }
}
